import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRefresh,
  faSearch,
  faFilter,
  faEye,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid } from 'date-fns';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import AbiActivityLogApiUrls from '../../../apiUrls/AbiActivityLogApiUrls';
import Spinner from '../../../components/era-package/era/Spinner';
import TextField from '../../../components/era-package/era/DebouncedTextField';
import Button from '../../../components/era-package/forms/Button';
import { Filter } from '../../../components/era-package/filters';
import ActivityLogDetailDialog from './ActivityLogDetailDialog';

const columnHelper = createColumnHelper();

const EMPTY_COL_PARAMS = {
  action: '',
  resourceType: '',
};

const ActivityLogList = () => {
  const theme = useTheme();

  const [data, setData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [columnParams, setColumnParams] = useState({ ...EMPTY_COL_PARAMS });
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [detailId, setDetailId] = useState(null);

  const fetchData = useCallback(
    async (pageIndex, pageSize, search, dateStart, dateEnd, colParams) => {
      try {
        setIsRefreshing(true);
        const response = await fetch(AbiActivityLogApiUrls.LIST, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
          },
          body: JSON.stringify({
            page: pageIndex,
            size: pageSize,
            ...(search && { search }),
            ...(dateStart && { startDate: dateStart }),
            ...(dateEnd && { endDate: dateEnd }),
            ...Object.fromEntries(
              Object.entries(colParams || {}).filter(([, v]) => v !== '')
            ),
          }),
        });
        const json = await response.json();
        setData(json.data?.content || json.content || []);
        setTotalElements(json.data?.totalElements || json.totalElements || 0);
      } catch (error) {
        console.error('Error fetching activity logs:', error);
        setData([]);
        setTotalElements(0);
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchData(
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
      startDate,
      endDate,
      columnParams
    );
  }, [fetchData, pagination.pageIndex, pagination.pageSize, globalFilter, startDate, endDate, columnParams]);

  const handleRefresh = useCallback(() => {
    fetchData(
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
      startDate,
      endDate,
      columnParams
    );
  }, [fetchData, pagination.pageIndex, pagination.pageSize, globalFilter, startDate, endDate, columnParams]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('username', {
        header: 'Username',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('action', {
        header: 'Action',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('resourceType', {
        header: 'Module Type',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('resourceName', {
        header: 'Reference ID',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('createdDate', {
        header: 'Created Date',
        cell: (info) => {
          const val = info.getValue();
          if (!val) return '-';
          const d = new Date(val);
          return isValid(d) ? format(d, 'dd MMM yyyy, HH:mm:ss') : val;
        },
        filterFn: 'includesString',
        meta: { filterVariant: 'dateRange' },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Action',
        cell: (info) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setDetailId(info.row.original.id);
            }}
          >
            <FontAwesomeIcon icon={faEye} width={16} />
          </IconButton>
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: {
      columnFilters,
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualFiltering: true,
    pageCount: Math.ceil(totalElements / pagination.pageSize),
    onPaginationChange: setPagination,
    onColumnFiltersChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnFilters) : updater;
      setColumnFilters(next);

      let dateStart = '';
      let dateEnd = '';
      const newColParams = { ...EMPTY_COL_PARAMS };

      next.forEach(({ id, value }) => {
        if (id === 'createdDate' && Array.isArray(value)) {
          const [start, end] = value;
          dateStart = start && isValid(start) ? format(start, 'yyyy-MM-dd') : '';
          dateEnd = end && isValid(end) ? format(end, 'yyyy-MM-dd') : '';
        } else if (id in newColParams) {
          newColParams[id] = value ?? '';
        }
      });

      setStartDate(dateStart);
      setEndDate(dateEnd);
      setColumnParams(newColParams);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
  });

  if (loading) {
    return (
      <div style={{ position: 'relative', height: '200px' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <TextField
            placeholder="Search..."
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faSearch} width="14px" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: { xs: '100%', sm: '300px' } }}
          />
          <Button
            startIcon={<FontAwesomeIcon icon={faRefresh} width="15" />}
            variant="outlined"
            onClick={handleRefresh}
            disabled={isRefreshing}
            color="success"
          >
            Refresh
          </Button>
          {isRefreshing && <CircularProgress size={20} />}
          <Button
            variant="outlined"
            onClick={() => setShowFilters((prev) => !prev)}
            startIcon={<FontAwesomeIcon icon={faFilter} style={{ width: 16, height: 16 }} />}
          >
            {showFilters ? 'Hide Filters' : 'Show Column Filters'}
          </Button>
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <React.Fragment key={headerGroup.id}>
                  <TableRow>
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          cursor: header.column.getCanSort() ? 'pointer' : 'default',
                          userSelect: 'none',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            backgroundColor: header.column.getCanSort()
                              ? theme.palette.action.hover
                              : 'transparent',
                          },
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() && (
                            <Box sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                              {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                  {showFilters && (
                    <TableRow>
                      {headerGroup.headers.map((header) => (
                        <TableCell key={`filter-${header.id}`}>
                          {header.column.getCanFilter() ? (
                            <Filter column={header.column} />
                          ) : null}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} hover>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} sx={{ whiteSpace: 'nowrap' }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography variant="body2" color={theme.palette.info.main}>
                      No activity logs found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalElements > 0 && (
          <Box
            width="100%"
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            alignSelf="flex-end"
            justifyContent="space-between"
            alignItems="center"
            gap={{ xs: 0, md: 2 }}
          >
            <Typography variant="h6">Total {totalElements} Records</Typography>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={totalElements}
              rowsPerPage={pagination.pageSize}
              page={pagination.pageIndex}
              onPageChange={(event, newPage) => {
                setPagination((prev) => ({ ...prev, pageIndex: newPage }));
              }}
              onRowsPerPageChange={(event) => {
                setPagination((prev) => ({
                  ...prev,
                  pageSize: parseInt(event.target.value, 10),
                  pageIndex: 0,
                }));
              }}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              sx={{ mt: '0!important' }}
            />
          </Box>
        )}
      </Stack>

      <ActivityLogDetailDialog
        open={detailId !== null}
        id={detailId}
        onClose={() => setDetailId(null)}
      />
    </LocalizationProvider>
  );
};

export default ActivityLogList;
